import { useMemo } from "react";
import {
  buildAutoComposition,
  findComposition,
  resolveComposition,
  ResolvedComposition,
} from "../helpers/composition";
import { keepIdentity } from "../helpers/identity";
import { MeetingSourceExtended, useMeetingSources, withShareScreenOffByDefault } from "./meeting";
import { useSettings } from "./settings";

/**
 * Sources declared by a composition (locally or inline) never go through
 * `buildMeetingSources`, so they get the same treatment here: a default
 * `extra.type`, the capture's own screen sharing off by default, and above
 * all a stable identity — the SDK's
 * `MeetingSourceElement` drops its live tracks whenever the `source` prop
 * changes identity, and the resolved settings hand out a fresh deep copy on
 * every update (see helpers/identity.ts).
 */
const normalizedSources = new Map<string, MeetingSourceExtended>();

const normalizeCompositionSource = (source: Partial<MeetingSourceExtended>, key: string): MeetingSourceExtended => {
  const next = withShareScreenOffByDefault({
    ...source,
    name: source.name ?? key,
    extra: source.extra?.type ? source.extra : { ...source.extra, type: "camera" },
  } as MeetingSourceExtended);
  const stable = keepIdentity(normalizedSources.get(key), next);
  normalizedSources.set(key, stable);
  return stable;
};

export interface UseCompositionResult {
  composition?: ResolvedComposition;
  /** An id was asked for and no composition carries it. */
  notFound: boolean;
}

/**
 * The composition `/composition/:id` renders. Without an id, the first
 * configured one; with no `settings.compositions` at all, an automatic grid of
 * every source that is not explicitly hidden.
 */
export const useComposition = (id: string | undefined): UseCompositionResult => {
  const [settings] = useSettings();
  const sources = useMeetingSources();
  const compositions = settings.compositions;

  return useMemo(() => {
    const options = { sources, normalizeSource: normalizeCompositionSource };
    const configured = findComposition(compositions, id);
    if (configured) return { composition: resolveComposition(configured, options), notFound: false };
    // Nothing configured: the automatic grid is a sensible landing page — but
    // only for a URL that did not ask for a precise composition.
    if (compositions?.length || id) return { notFound: true };
    return { composition: resolveComposition(buildAutoComposition(sources), options), notFound: false };
  }, [compositions, id, sources]);
};
